import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
	FiArrowRight,
	FiMenu,
	FiX,
	FiRotateCcw,
	FiMail,
	FiUsers,
	FiPlus,
	FiGift,
} from "react-icons/fi";
import {
	FaPlane,
	FaMusic,
	FaUtensils,
	FaVolleyballBall,
	FaDice,
	FaStore,
} from "react-icons/fa";
import { GiCactus } from "react-icons/gi";
import "./snowbird.css";
import ShareWebsite from "./ShareWebsite";

const assets = `${process.env.PUBLIC_URL}/logo-preview`;
const registrationUrl =
	"https://volleyballlife.com/event/41702?tab=information";
const links = [
	["Tournament", "tournament"],
	["Hawaii Prize", "hawaii-prize"],
	["Travel", "travel"],
	["FAQ", "faq"],
];

function Logo({ animated = false }) {
	const stage = useRef(null);
	const [replay, setReplay] = useState(0);
	useEffect(() => {
		if (!animated || !window.SnowbirdTimeline) return;
		const timeline = window.SnowbirdTimeline;
		const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
		const images = [...stage.current.querySelectorAll("img")];
		let request;
		let disposed = false;
		let start;
		const render = (frame) => {
			const pose = timeline.at(frame);
			images.forEach((image) => {
				const p = pose[image.dataset.layer];
				image.style.transform = `translate(${(p.x / timeline.WIDTH) * 100}%, ${(p.y / timeline.HEIGHT) * 100}%) rotate(${p.rotation}deg)`;
				image.style.visibility = p.visible ? "visible" : "hidden";
			});
		};
		const tick = (now) => {
			if (start === undefined) start = now;
			const frame = Math.min(55, ((now - start) / 1000) * timeline.FPS);
			render(frame);
			if (frame < 55) request = requestAnimationFrame(tick);
		};
		const finish = () => {
			cancelAnimationFrame(request);
			render(55);
		};
		const onPreference = () => {
			if (preference.matches) finish();
		};
		const onVisibility = () => {
			if (document.hidden) finish();
		};
		Promise.all(images.map((image) => image.decode()))
			.then(() => {
				if (disposed || preference.matches) return;
				request = requestAnimationFrame(tick);
			})
			.catch(finish);
		preference.addEventListener("change", onPreference);
		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			disposed = true;
			cancelAnimationFrame(request);
			preference.removeEventListener("change", onPreference);
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [animated, replay]);
	return (
		<div className={`sb-logo ${animated ? "sb-logo-hero" : ""}`} ref={stage}>
			<div role="img" aria-label="Snowbird Showdown">
				{["rv", "ball", "text"].map((layer) => (
					<img
						key={layer}
						data-layer={layer}
						src={`${assets}/assets/${{ rv: "RV", ball: "BALL", text: "Text" }[layer]}.svg`}
						alt=""
					/>
				))}
			</div>
			{animated && (
				<button
					className="sb-replay"
					aria-label="Replay logo animation"
					title="Replay logo animation"
					onClick={() => setReplay((value) => value + 1)}
				>
					<FiRotateCcw />
				</button>
			)}
		</div>
	);
}

function Flight() {
	const route = useRef(null);
	const path = useRef(null);
	const plane = useRef(null);
	useEffect(() => {
		let request;
		const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
		const position = (progress) => {
			const length = path.current.getTotalLength();
			const point = path.current.getPointAtLength(progress * length);
			const ahead = path.current.getPointAtLength(
				Math.min(length, progress * length + 1),
			);
			const angle =
				progress === 1
					? 46
					: (Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180) / Math.PI;
			plane.current.setAttribute(
				"transform",
				`translate(${point.x} ${point.y}) rotate(${angle})`,
			);
		};
		position(1);
		const finish = () => {
			cancelAnimationFrame(request);
			position(1);
		};
		const onPreference = () => {
			if (preference.matches) finish();
		};
		preference.addEventListener("change", onPreference);
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					if (!preference.matches) {
						let start;
						const tick = (now) => {
							if (start === undefined) start = now;
							const progress = Math.min(1, (now - start) / 3000);
							position(progress * progress * (3 - 2 * progress));
							if (progress < 1) request = requestAnimationFrame(tick);
						};
						request = requestAnimationFrame(tick);
					}
					observer.disconnect();
				}
			},
			{ threshold: 0.5 },
		);
		observer.observe(route.current);
		return () => {
			observer.disconnect();
			cancelAnimationFrame(request);
			preference.removeEventListener("change", onPreference);
		};
	}, []);
	return (
		<div
			className="sb-flight"
			ref={route}
			aria-label="Flight from Tucson to Hawaii"
		>
			<svg viewBox="0 0 600 180" aria-hidden="true">
				<path
					ref={path}
					d="M40 145 C170 -10 410 -10 560 145"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeDasharray="5 8"
				/>
				<g
					ref={plane}
					transform="translate(560 145) rotate(46)"
					style={{ color: "#98493a" }}
				>
					<FaPlane x="-16" y="-16" width="32" height="32" />
				</g>
			</svg>
			<div className="sb-airports">
				<span>
					TUCSON<small>Win here.</small>
				</span>
				<span>
					HAWAII<small>Play there.</small>
				</span>
			</div>
		</div>
	);
}

const fieldAreas = [
	{
		name: "Courts",
		icon: FaVolleyballBall,
		x: 50,
		y: 48,
		title: "Find your game.",
		copy: "Play your matches, cheer on your friends, and catch the action around the courts.",
	},
	{
		name: "Music",
		icon: FaMusic,
		x: 19,
		y: 20,
		title: "Follow the music.",
		copy: "Take a break from the competition and enjoy live music with your crew. Performer details are coming soon.",
	},
	{
		name: "Concessions",
		icon: FaUtensils,
		x: 81,
		y: 24,
		title: "Take a break between matches.",
		copy: "On-site concessions are available. See the FAQ for food and drink guidelines.",
	},
	{
		name: "Games",
		icon: FaDice,
		x: 19,
		y: 66,
		title: "A little friendly competition.",
		copy: "Keep the fun going off the court with lawn games and time together.",
	},
	{
		name: "Vendors",
		icon: FaStore,
		x: 81,
		y: 70,
		title: "Take a look around.",
		copy: "Meet local vendors and browse their finds. The vendor lineup is still being finalized.",
	},
];

function FieldMap() {
	const [selected, setSelected] = useState(0);
	const map = useRef(null);
	const selectArea = (index) => {
		setSelected(index);
		if (!window.matchMedia("(max-width: 700px)").matches || !map.current)
			return;
		const headerHeight =
			document.querySelector(".sb-header")?.getBoundingClientRect().height || 0;
		window.scrollTo({
			top: Math.max(
				0,
				window.scrollY +
					map.current.getBoundingClientRect().top -
					headerHeight -
					12,
			),
			behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
				? "instant"
				: "smooth",
		});
	};
	const area = fieldAreas[selected];
	return (
		<section className="sb-day" id="your-day" aria-labelledby="sb-day-title">
			<svg
				className="sb-day-curve"
				viewBox="0 0 1440 100"
				preserveAspectRatio="none"
				aria-hidden="true"
			>
				<path d="M0 50 C350 -30 760 130 1100 45 S1330 25 1440 50 L1440 100 L0 100Z" />
			</svg>
			<div className="sb-inner">
				<p className="sb-eyebrow">
					A LITTLE COMPETITION. A WHOLE LOT OF GOOD COMPANY.
				</p>
				<h2 id="sb-day-title">Your day at the Showdown.</h2>
				<p className="sb-day-intro">
					From your first serve to the final point, there's more to explore.
				</p>
				<div className="sb-map-explorer">
					<div className="sb-field-map" ref={map}>
						<img
							src={`${assets}/field-map-concessions.png`}
							width="1536"
							height="1024"
							loading="lazy"
							alt="Conceptual festival layout with volleyball courts in the center, music and on-site concessions above, and games and vendors below. Not the final venue map."
						/>
						{fieldAreas.map((item, index) => (
							<button
								key={item.name}
								className="sb-map-pin"
								style={{ left: `${item.x}%`, top: `${item.y}%` }}
								aria-label={item.name}
								aria-pressed={selected === index}
								aria-controls="sb-map-detail"
								onClick={() => selectArea(index)}
								title={item.name}
							>
								<item.icon aria-hidden="true" />
							</button>
						))}
					</div>
					<div className="sb-map-sidebar">
						<div
							className="sb-map-detail"
							id="sb-map-detail"
							aria-live="polite"
							aria-atomic="true"
						>
							<p className="sb-map-area">
								<area.icon aria-hidden="true" />
								{area.name}
							</p>
							<h3>{area.title}</h3>
							<p>{area.copy}</p>
						</div>
						<div
							className="sb-map-legend"
							role="group"
							aria-label="Explore the event areas"
						>
							{fieldAreas.map((item, index) => (
								<button
									key={item.name}
									aria-pressed={selected === index}
									aria-controls="sb-map-detail"
									onClick={() => selectArea(index)}
								>
									<item.icon aria-hidden="true" />
									{item.name}
								</button>
							))}
						</div>
					</div>
				</div>
				<p className="sb-map-note">
					An illustration of the experience, not the final venue layout.
					Locations and event details will be confirmed before the tournament.
				</p>
			</div>
		</section>
	);
}

export default function SnowbirdPage() {
	const [menu, setMenu] = useState(false);
	const menuButton = useRef(null);
	return (
		<div className="snowbird">
			<Helmet>
				<title>Snowbird Showdown | Tucson Grass Volleyball</title>
				<meta
					name="description"
					content="Two days of grass volleyball in Tucson, January 1 and 2. Meet the Snowbird Showdown, in partnership with the Snoop Dogg Arizona Bowl and 7Gen."
				/>
			</Helmet>
			<a className="sb-skip" href="#tournament">
				Skip to tournament details
			</a>
			<header
				className="sb-header"
				onKeyDown={(event) => {
					if (event.key === "Escape") {
						setMenu(false);
						menuButton.current.focus();
					}
				}}
			>
				<a href="#top" className="sb-brand" aria-label="Snowbird Showdown home">
					<Logo />
				</a>
				<nav
					id="snowbird-menu"
					className={menu ? "sb-nav is-open" : "sb-nav"}
					aria-label="Main navigation"
				>
					{links.map(([label, id]) => (
						<a key={id} href={`#${id}`} onClick={() => setMenu(false)}>
							{label}
						</a>
					))}
				</nav>
				<a className="sb-button sb-nav-cta" href={registrationUrl}>
					Register <FiArrowRight />
				</a>
				<div className="sb-header-date">
					<GiCactus aria-hidden="true" />
					<div>
						<strong>JANUARY 1 + 2</strong>
						<span>TUCSON, ARIZONA</span>
					</div>
				</div>
				<button
					ref={menuButton}
					className="sb-menu"
					aria-label={menu ? "Close menu" : "Open menu"}
					aria-controls="snowbird-menu"
					aria-expanded={menu}
					onClick={() => setMenu(!menu)}
				>
					{menu ? <FiX /> : <FiMenu />}
				</button>
			</header>
			<main id="top">
				<section className="sb-hero" aria-labelledby="sb-title">
					<img
						className="sb-hero-photo"
						src={`${assets}/snowbird-hero-placeholder.png`}
						alt=""
						fetchPriority="high"
					/>
					<div className="sb-hero-content">
						<h1 id="sb-title" className="sb-sr-only">
							Snowbird Showdown
						</h1>
						<Logo animated />
						<p className="sb-eyebrow">JANUARY 1 + 2 / TUCSON, ARIZONA</p>
						<p className="sb-hero-line">Start the year on the grass.</p>
						<a className="sb-button" href={registrationUrl}>
							Register now <FiArrowRight />
						</a>
					</div>
				</section>
				<div className="sb-partnership">
					<p className="sb-partners">
						<span className="sb-partners-label">IN PARTNERSHIP WITH</span>
						<strong>Snoop Dogg Arizona Bowl</strong>
						<span aria-hidden="true">+</span>
						<strong>7Gen</strong>
					</p>
					<p className="sb-partner-benefit">
						Every registered athlete gets a{" "}
						<strong>FREE Arizona Bowl game ticket.</strong>{" "}
						<a href="#bowl-weekend">
							Bowl Bash + ticket details <FiArrowRight aria-hidden="true" />
						</a>
					</p>
					<aside
						className="sb-halloween-teaser"
						aria-labelledby="sb-halloween-title"
					>
						<div>
							<p className="sb-eyebrow">
								FIRST, A HALLOWEEN WARM-UP / OCTOBER 31
							</p>
							<h2 id="sb-halloween-title">Halloween Coed 4v4</h2>
							<p>
								Coed A &amp; B at Kino South. $60 per team. Bring a group
								costume for a chance to win $100.
							</p>
						</div>
						<a href="/halloween">
							Halloween event details <FiArrowRight aria-hidden="true" />
						</a>
					</aside>
				</div>
				<section
					className="sb-experience"
					id="off-the-court"
					aria-labelledby="sb-experience-title"
				>
					<svg
						className="sb-experience-curve"
						viewBox="0 0 1440 100"
						preserveAspectRatio="none"
						aria-hidden="true"
					>
						<path d="M0 64 C320 130 550 -15 890 35 S1250 115 1440 40 L1440 100 L0 100Z" />
					</svg>
					<div className="sb-inner">
						<p className="sb-eyebrow">MORE THAN A TOURNAMENT</p>
						<h2 id="sb-experience-title">
							Off the court.
							<br />
							<em>Into the fun.</em>
						</h2>
						<p className="sb-experience-intro">
							Between matches or just here to hang, make a day of it with your
							favorite people.
						</p>
						<div className="sb-experience-grid">
							{[
								[
									"Live music",
									"The soundtrack to your day on the grass.",
									"Local musicians performing outdoors",
									"0% 0%",
								],
								[
									"Play a little",
									"Keep the friendly competition going with lawn games.",
									"Friends playing lawn games on the grass",
									"0% 100%",
								],
								[
									"Meet the makers",
									"Explore local vendors and discover something good.",
									"Visitors browsing a local artisan market",
									"100% 100%",
								],
							].map(([title, copy, description, position], index) => {
								const Icon = [FaMusic, FaDice, FaStore][index];
								return (
									<article className="sb-experience-feature" key={title}>
										<div
											className="sb-experience-photo"
											role="img"
											aria-label={`Illustration: ${description}`}
											style={{
												backgroundImage: `url(${assets}/off-court-placeholder.png)`,
												backgroundPosition: position,
											}}
										/>
										<h3>
											<Icon aria-hidden="true" />
											<span>{title}</span>
										</h3>
										<p>{copy}</p>
									</article>
								);
							})}
						</div>
						<p className="sb-experience-note">
							Music, games, and vendors are in the works. Lineup and details
							coming soon.
						</p>
					</div>
				</section>
				<FieldMap />
				<section
					className="sb-crew"
					id="sideline-crew"
					aria-labelledby="sb-crew-title"
				>
					<svg
						className="sb-crew-curve"
						viewBox="0 0 1440 100"
						preserveAspectRatio="none"
						aria-hidden="true"
					>
						<path d="M0 65 C270 125 540 -20 850 35 S1210 110 1440 35 L1440 100 L0 100Z" />
					</svg>
					<div className="sb-inner">
						<p className="sb-eyebrow">GOOD COMPANY BELONGS HERE.</p>
						<h2 id="sb-crew-title">
							Bring your
							<br />
							<em>sideline crew.</em>
						</h2>
						<p className="sb-crew-intro">
							Not everyone needs a jersey. Bring the friends who cheer the
							loudest, the family who never misses a match, and the people who
							make a day outside even better.
						</p>
						<figure className="sb-crew-photo">
							<img
								src={`${assets}/sideline-placeholder.png`}
								width="1536"
								height="1024"
								loading="lazy"
								alt="Illustrative scene of friends and family cheering together beside grass volleyball courts in Tucson"
							/>
						</figure>
						<div className="sb-crew-visit">
							<div>
								<h3>Your people. Your kind of day.</h3>
								<p>
									Come for the volleyball. Stay for the music and time together.
								</p>
							</div>
							<a className="sb-button" href="#travel">
								Plan your visit <FiArrowRight aria-hidden="true" />
							</a>
						</div>
						<p className="sb-crew-note">
							Spectator admission, venue guidelines, and arrival details are
							coming soon.
						</p>
					</div>
				</section>
				<section
					className="sb-bowl"
					id="bowl-weekend"
					aria-labelledby="sb-bowl-title"
				>
					<svg
						className="sb-bowl-curve"
						viewBox="0 0 1440 100"
						preserveAspectRatio="none"
						aria-hidden="true"
					>
						<path d="M0 60 C380 -30 820 140 1440 30 V100 H0Z" />
					</svg>
					<div className="sb-inner">
						<p className="sb-eyebrow">VOLLEYBALL MEETS BOWL WEEKEND</p>
						<div className="sb-bowl-layout">
							<div>
								<h2 id="sb-bowl-title">
									Make a New Year's
									<br />
									weekend of it.
								</h2>
								<p className="sb-lead">
									Start with the Arizona Bowl's Bowl Bash on New Year's Eve. Hit
									the grass on January 1. Then cheer on the championship games
									at the Arizona Bowl tailgate on January 2.
								</p>
								<p>
									And there's football, too. Every registered athlete gets a
									free ticket to the Arizona Bowl game.
								</p>
								<a className="sb-text-link" href="#travel">
									See the weekend plan <FiArrowRight aria-hidden="true" />
								</a>
							</div>
							<div className="sb-bowl-ticket">
								<FiGift aria-hidden="true" />
								<p>INCLUDED WITH ATHLETE REGISTRATION</p>
								<h3>
									You're going
									<br />
									to the Bowl.
								</h3>
								<strong>FREE GAME TICKET</strong>
								<span>Snoop Dogg Arizona Bowl</span>
							</div>
						</div>
						<dl className="sb-ticket-details">
							<div>
								<dt>
									<FiMail aria-hidden="true" />
									Straight to your inbox
								</dt>
								<dd>
									Your ticket goes directly to the email you use to register.
								</dd>
							</div>
							<div>
								<dt>
									<FiPlus aria-hidden="true" />
									Bring a few more fans
								</dt>
								<dd>Buy extra game tickets during registration.</dd>
							</div>
							<div>
								<dt>
									<FiUsers aria-hidden="true" />
									Keep your team together
								</dt>
								<dd>
									All tickets purchased with your team will be seated together.
								</dd>
							</div>
						</dl>
						<p className="sb-bowl-note">
							Bowl Bash location and hours, plus Bowl game date and kickoff
							time, will be posted once confirmed.
						</p>
					</div>
				</section>
				<section
					className="sb-prize"
					id="hawaii-prize"
					aria-labelledby="sb-prize-title"
				>
					<svg
						className="sb-curve"
						viewBox="0 0 1440 120"
						preserveAspectRatio="none"
						aria-hidden="true"
					>
						<path d="M0 90 C400 -90 950 190 1440 15 L1440 120 L0 120Z" />
					</svg>
					<div className="sb-inner">
						<div className="sb-prize-copy">
							<div>
								<p className="sb-eyebrow">MEN'S OPEN + WOMEN'S OPEN WINNERS</p>
								<h2 id="sb-prize-title">
									Next stop:
									<br />
									<em>Hawaii.</em>
								</h2>
								<p className="sb-lead">
									Win <strong>Men's Open</strong> or{" "}
									<strong>Women's Open</strong> and head to Hawaii for the{" "}
									<strong>7Gen Luau Grass Tournament in July.</strong>
								</p>
								<ul
									className="sb-prize-inclusions"
									aria-label="Open division winner prizes"
								>
									<li>
										<strong>FREE</strong> flight
									</li>
									<li>
										<strong>FREE</strong> hotel
									</li>
									<li>
										<strong>FREE</strong> tournament entry
									</li>
								</ul>
							</div>
							<div className="sb-prize-route">
								<Flight />
								<p className="sb-caption">
									Two Open divisions. A Hawaii prize for each winner.
									<br />
									See <a href={registrationUrl}>VolleyballLife</a> for current
									event details. Full prize terms and coverage details are still
									to be confirmed.
								</p>
							</div>
						</div>
					</div>
					<figure className="sb-hawaii-photo">
						<svg
							className="sb-hawaii-wave"
							viewBox="0 0 1440 80"
							preserveAspectRatio="none"
							aria-hidden="true"
						>
							<path d="M0 0 H1440 V24 C1080 110 730 -20 380 48 S80 60 0 32Z" />
						</svg>
						<img
							src={`${assets}/hawaii-placeholder.png`}
							width="1536"
							height="1024"
							loading="lazy"
							alt="Illustrative Hawaiian coastline framed by palms, with turquoise water and green mountains"
						/>
						<figcaption>
							Hawaii inspiration. Generated placeholder, not the tournament
							venue.
						</figcaption>
					</figure>
				</section>
				<section
					className="sb-tournament sb-band"
					id="tournament"
					aria-labelledby="sb-tournament-title"
				>
					<div className="sb-inner">
						<p className="sb-eyebrow">THE TOURNAMENT</p>
						<h2 id="sb-tournament-title">
							Find your division.
							<br />
							Bring your game.
						</h2>
						<div className="sb-entry">
							<strong>$50</strong>
							<span>
								per player
								<br />
								All divisions
							</span>
						</div>
						<p className="sb-entry-ticket">
							<FiGift aria-hidden="true" />
							Includes a FREE Arizona Bowl game ticket.{" "}
							<a href="#bowl-weekend">
								Ticket details <FiArrowRight aria-hidden="true" />
							</a>
						</p>
						<div className="sb-division-groups">
							{[
								["Open", "3v3", ["Men's Open", "Women's Open"]],
								["Co-Ed", "4v4", ["Co-Ed A/B", "Co-Ed C"]],
								["Juniors", "3v3", ["Boys Under 18", "Girls Under 18"]],
							].map(([group, format, divisions]) => (
								<div key={group} className="sb-division-group">
									<h3>
										{group}
										<span>{format}</span>
									</h3>
									<ul>
										{divisions.map((division) => (
											<li key={division}>{division}</li>
										))}
									</ul>
								</div>
							))}
						</div>
						<div className="sb-play-days">
							<article>
								<p className="sb-eyebrow">JANUARY 1</p>
								<h3>Everyone plays.</h3>
								<p className="sb-venue">Kino South Complex</p>
								<p>All six divisions take the grass in Tucson.</p>
								<small>Start times TBD</small>
							</article>
							<article>
								<p className="sb-eyebrow">JANUARY 2</p>
								<h3>Championship day.</h3>
								<p className="sb-venue">Arizona Mall</p>
								<p>Championship games only, at the Arizona Bowl tailgate.</p>
								<small>Start times TBD</small>
							</article>
						</div>
						<p className="sb-tournament-note">
							Register your team on VolleyballLife. The full match schedule will
							be posted closer to the event.
						</p>
						<a className="sb-button" href={registrationUrl}>
							Choose your division <FiArrowRight />
						</a>
					</div>
				</section>
				<section className="sb-travel sb-band" id="travel">
					<div className="sb-inner">
						<p className="sb-eyebrow">MAKE THE TRIP</p>
						<h2>
							Three days.
							<br />
							One Tucson getaway.
						</h2>
						<ol className="sb-weekend-plan">
							<li>
								<p className="sb-eyebrow">DECEMBER 31</p>
								<h3>Kick off with Bowl Bash.</h3>
								<p>
									Celebrate New Year's Eve with the Arizona Bowl before
									tournament play begins.
								</p>
								<small>Location and times coming soon</small>
							</li>
							<li>
								<p className="sb-eyebrow">JANUARY 1</p>
								<h3>Take the grass.</h3>
								<p>All divisions play at Kino South Complex.</p>
								<small>Match and check-in times TBD</small>
							</li>
							<li>
								<p className="sb-eyebrow">JANUARY 2</p>
								<h3>Finish at the tailgate.</h3>
								<p>
									Championship games only, on the Arizona Mall at the Arizona
									Bowl tailgate.
								</p>
								<small>Championship times TBD</small>
							</li>
						</ol>
						<p className="sb-lead">
							Your Arizona Bowl game ticket is included with athlete
							registration. Game date and kickoff time will be added here once
							confirmed.
						</p>
						<p>Directions, parking, and lodging information are coming soon.</p>
						<a className="sb-text-link" href="mailto:director@sidewinderva.com">
							Questions about the trip? <FiArrowRight />
						</a>
					</div>
				</section>
				<section className="sb-faq sb-band" id="faq">
					<div className="sb-inner">
						<p className="sb-eyebrow">BEFORE YOU PACK</p>
						<h2>A few good questions.</h2>
						<details>
							<summary>Can I bring food or drinks?</summary>
							<p>
								On-site concessions are available. Outside food and drinks are
								not allowed, but water is permitted.
							</p>
						</details>
						<details>
							<summary>How do we register?</summary>
							<p>
								Entry is $50 per player in all divisions.{" "}
								<a href={registrationUrl}>Register on VolleyballLife</a> and
								check the event listing for current registration details and
								deadlines.
							</p>
						</details>
						{[
							[
								"Where and when are we playing?",
								"Everyone plays January 1 at Kino South Complex. Only championship games take place January 2, on the Arizona Mall at the Arizona Bowl tailgate. Start times are TBD.",
							],
							[
								"What is the Hawaii prize?",
								"The winners of Men's Open and Women's Open each receive a FREE flight, FREE hotel, and FREE entry into the 7Gen Luau Grass Tournament in Hawaii in July. Full prize terms and coverage details are still to be confirmed.",
							],
							[
								"Can we bring an RV?",
								"The RV is part of the Snowbird spirit. On-site parking and camping have not been confirmed; please check back before making those arrangements.",
							],
						].map(([question, answer]) => (
							<details key={question}>
								<summary>{question}</summary>
								<p>{answer}</p>
							</details>
						))}
					</div>
				</section>
				<section className="sb-registration sb-band" id="registration">
					<div className="sb-inner">
						<p className="sb-eyebrow">SEE YOU ON THE GRASS</p>
						<h2>Get your team together.</h2>
						<p>
							Registration is open on VolleyballLife. Choose your division and
							get ready for Tucson.
						</p>
						<a className="sb-button" href={registrationUrl}>
							Register now <FiArrowRight />
						</a>
						<ShareWebsite />
					</div>
				</section>
			</main>
			<footer className="sb-footer">
				<span>
					Snowbird Showdown <small>By Sidewinder Volleyball Academy</small>
				</span>
				<a href="/archive">Past grass events</a>
				<a href="https://sidewinderva.com">Sidewinder</a>
			</footer>
		</div>
	);
}
